/**
 * I filtri della mappa, sopra la carta. Due file di interruttori:
 *  - QUALI AZIENDE (solo nella console): di predefinito le attive (Simone, 27/10/2026: «di default solo le aziende che
 *    a livello di tenant sono attive»); si aggiungono pagamenti in ritardo, sospese, bloccate, cancellate, o «Tutte».
 *    Compaiono solo gli stati che hanno almeno un'azienda.
 *  - I LIVELLI, che sono anche la legenda dei colori: il pallino è quello della mappa.
 * Sul telefono ogni fila scorre di lato invece di andare a capo.
 */
import React from 'react';
import { Box, ButtonBase, Typography, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LIVELLI_MAPPA, STATI_MAPPA, type LivelloMappa, type StatoMappa } from '@shared/admin/mappaAziende';
import { tokens } from '../../theme';
import { COLORE_LIVELLO } from './aspetto';
import { PallinoAdesso } from './PallinoAdesso';

interface Props {
  mostraStati: boolean;
  contiStati: Record<StatoMappa, number>;
  stati: ReadonlySet<StatoMappa>;
  onStati: (stati: Set<StatoMappa>) => void;
  contiLivelli: Record<LivelloMappa, number>;
  livelli: ReadonlySet<LivelloMappa>;
  onLivelli: (livelli: Set<LivelloMappa>) => void;
  /** Quante aziende hanno qualcuno al lavoro adesso (fra quelle degli stati scelti). */
  aziendeAdesso: number;
  soloAdesso: boolean;
  onSoloAdesso: (solo: boolean) => void;
}

const Interruttore: React.FC<{
  acceso: boolean;
  onClick: () => void;
  pallino?: React.ReactNode;
  testo: string;
  numero?: number;
}> = ({ acceso, onClick, pallino, testo, numero }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={acceso}
    sx={{
      flexShrink: 0,
      minHeight: 38,
      px: 1.5,
      gap: 0.85,
      borderRadius: `${tokens.radius.pill}px`,
      border: `1px solid ${acceso ? tokens.color.graphite : tokens.color.line}`,
      bgcolor: acceso ? tokens.color.paper : alpha(tokens.color.paper, 0.7),
      color: acceso ? tokens.color.graphite : tokens.color.ash,
      fontFamily: tokens.font.body,
      fontSize: '0.8rem',
      fontWeight: 600,
      transition: 'border-color 140ms ease, background-color 140ms ease, color 140ms ease',
      '&:hover': { borderColor: tokens.color.graphite },
      '&.Mui-focusVisible': { outline: `2px solid ${tokens.color.graphite}`, outlineOffset: 2 },
    }}
  >
    {pallino}
    <span>{testo}</span>
    {numero !== undefined && (
      <Box component="span" sx={{ color: acceso ? tokens.color.ash : tokens.color.mist, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{numero}</Box>
    )}
  </ButtonBase>
);

const Pallino: React.FC<{ livello: LivelloMappa; acceso: boolean }> = ({ livello, acceso }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      border: `2px solid ${acceso ? COLORE_LIVELLO[livello] : tokens.color.mist}`,
      bgcolor: livello === 'free' || !acceso ? 'transparent' : COLORE_LIVELLO[livello],
    }}
  />
);

const Fila: React.FC<{ etichetta: string; children: React.ReactNode }> = ({ etichetta, children }) => (
  <Box role="group" aria-label={etichetta} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Typography component="span" sx={{ fontSize: '0.75rem', color: tokens.color.ash, flexShrink: 0, mr: 0.25, display: { xs: 'none', sm: 'inline' } }}>
      {etichetta}
    </Typography>
    <Box
      sx={{
        display: 'flex',
        gap: 0.75,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        py: 0.25,
        px: 0.25,
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  </Box>
);

export const FiltriMappa: React.FC<Props> = ({
  mostraStati, contiStati, stati, onStati, contiLivelli, livelli, onLivelli, aziendeAdesso, soloAdesso, onSoloAdesso,
}) => {
  const { t } = useTranslation('mappa');
  const presenti = STATI_MAPPA.filter((s) => contiStati[s] > 0 || s === 'attiva');
  const tutte = presenti.every((s) => stati.has(s));
  const cambiaStato = (s: StatoMappa) => {
    const nuovi = new Set(stati);
    if (nuovi.has(s)) nuovi.delete(s);
    else nuovi.add(s);
    onStati(nuovi);
  };
  const cambiaLivello = (l: LivelloMappa) => {
    const nuovi = new Set(livelli);
    if (nuovi.has(l)) nuovi.delete(l);
    else nuovi.add(l);
    onLivelli(nuovi);
  };
  return (
    // Sul computer i gruppi stanno su una riga e vanno a capo interi quando non c'entrano (mai stretti fino a tagliare
    // un'etichetta); sul telefono uno sotto l'altro, e ognuno scorre di lato.
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, flexWrap: { lg: 'wrap' }, alignItems: { lg: 'center' }, columnGap: 2.5, rowGap: 0.75, minWidth: 0 }}>
      {mostraStati && presenti.length > 1 && (
        <Fila etichetta={t('filtri.stato')}>
          <Interruttore acceso={tutte} onClick={() => onStati(tutte ? new Set<StatoMappa>(['attiva']) : new Set(presenti))} testo={t('filtri.tutte')} />
          {presenti.map((s) => (
            <Interruttore key={s} acceso={stati.has(s)} onClick={() => cambiaStato(s)} testo={t(`stati.${s}`)} numero={contiStati[s]} />
          ))}
        </Fila>
      )}
      <Fila etichetta={t('filtri.livello')}>
        {LIVELLI_MAPPA.map((l) => (
          <Interruttore
            key={l}
            acceso={livelli.has(l)}
            onClick={() => cambiaLivello(l)}
            pallino={<Pallino livello={l} acceso={livelli.has(l)} />}
            testo={t(`livelli.${l}`)}
            numero={contiLivelli[l]}
          />
        ))}
      </Fila>
      <Fila etichetta={t('filtri.adesso_gruppo')}>
        <Interruttore
          acceso={soloAdesso}
          onClick={() => onSoloAdesso(!soloAdesso)}
          pallino={<PallinoAdesso spento={aziendeAdesso === 0} />}
          testo={t('filtri.adesso')}
          numero={aziendeAdesso}
        />
      </Fila>
    </Box>
  );
};
